<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const showPrompt = ref(false);
const deferredPrompt = ref(null);

const handleBeforeInstallPrompt = (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    
    // Stash the event so it can be triggered later
    deferredPrompt.value = e;
    
    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = localStorage.getItem('pwa-prompt-dismissed-time');
    
    // Show prompt again after 7 days
    if (dismissed && dismissedTime) {
        const daysSinceDismissal = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissal < 7) {
            return;
        }
    }
    
    // Show the install prompt
    showPrompt.value = true;
};

const installPwa = async () => {
    if (!deferredPrompt.value) {
        return;
    }
    
    // Show the install prompt
    deferredPrompt.value.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.value.userChoice;
    
    // We no longer need the prompt. Clear it up.
    deferredPrompt.value = null;
    showPrompt.value = false;
    
    // Optionally track the outcome
    if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
    }
};

const dismissPrompt = () => {
    showPrompt.value = false;
    localStorage.setItem('pwa-prompt-dismissed', 'true');
    localStorage.setItem('pwa-prompt-dismissed-time', Date.now().toString());
};

onMounted(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
        return;
    }
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
});

onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
});
</script>

<template>
    <Transition
        enter-active-class="transition ease-out duration-300"
        enter-from-class="translate-y-full opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition ease-in duration-200"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-full opacity-0"
    >
        <div
            v-if="showPrompt"
            class="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t-2 border-purple-600 shadow-2xl dark:bg-gray-800 dark:border-purple-500 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md sm:rounded-lg sm:border-2"
        >
            <div class="flex items-start gap-4">
                <!-- App Icon -->
                <div class="flex-shrink-0">
                    <img
                        src="/images/android-chrome-192x192.png"
                        alt="Spicy Collab"
                        class="w-16 h-16 rounded-lg shadow-md"
                    />
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                        Install Spicy Collab
                    </h3>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        Add to your home screen for quick access and a better experience!
                    </p>

                    <!-- Action Buttons -->
                    <div class="flex gap-3 mt-4">
                        <button
                            type="button"
                            @click="installPwa"
                            class="px-4 py-2 text-sm font-medium text-white transition-colors bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                        >
                            Install
                        </button>
                        <button
                            type="button"
                            @click="dismissPrompt"
                            class="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:focus:ring-offset-gray-800"
                        >
                            Not Now
                        </button>
                    </div>
                </div>

                <!-- Close Button -->
                <button
                    type="button"
                    @click="dismissPrompt"
                    class="flex-shrink-0 p-1 text-gray-400 transition-colors rounded-lg hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Dismiss"
                >
                    <svg
                        class="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    </Transition>
</template>
